'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  title: string;
  description: string;
  language: string;
  format: string;
  size: string;
  icon: string;
}

interface DownloadableResourcesProps {
  resources: Resource[];
}

const DownloadableResources = ({ resources }: DownloadableResourcesProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const languages = ['all', ...Array.from(new Set(resources.map(r => r.language)))];
  const filteredResources = selectedLanguage === 'all' 
    ? resources 
    : resources.filter(r => r.language === selectedLanguage);

  const handleDownload = (resource: Resource) => {
    alert(`Downloading ${resource.title} in ${resource.language}...`);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-natural">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
            Downloadable Resources
          </h3>
          <p className="text-text-secondary">
            Access comprehensive guides and infographics in multiple languages
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon name="LanguageIcon" size={20} className="text-text-secondary" />
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang === 'all' ? 'All Languages' : lang}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="grid sm:grid-cols-2 gap-4">
        {filteredResources.map((resource) => (
          <div
            key={resource.id}
            className="bg-surface border border-border rounded-lg p-6 hover:shadow-natural transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={resource.icon as any} size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-heading font-bold text-text-primary mb-1">
                    {resource.title}
                  </h4>
                  <p className="text-sm text-text-secondary">{resource.description}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <span className="flex items-center space-x-1">
                  <Icon name="LanguageIcon" size={16} />
                  <span>{resource.language}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="DocumentTextIcon" size={16} />
                  <span>{resource.format}</span>
                </span>
                <span>{resource.size}</span>
              </div>
              
              <button
                onClick={() => handleDownload(resource)}
                className="p-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="ArrowDownTrayIcon" size={20} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DownloadableResources;